import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Trophy, Home, BookOpen, Volume2, VolumeX, ArrowLeft, User, Gift, Loader2, Sparkles, Settings, ChevronDown, Mic } from 'lucide-react';
import { useGame } from '../../context/GameContext';
import { useRewards } from '../../context/RewardsContext';
import { PointsButton } from '../../components/RewardsWidgets';
import { getStoryData } from '../../utils/mockData';
import soundEffects from '../../utils/soundEffects';
import { usePremiumTTS, PREMIUM_VOICES } from '../../hooks/usePremiumTTS';
import CelebrationAnimation from '../../components/CelebrationAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoryGame = () => {
  const navigate = useNavigate();
  const { selectedAge, selectedSubject, addScore } = useGame();
  const { awardCorrectAnswer, awardLessonComplete } = useRewards();
  const [storyData, setStoryData] = useState(null);
  const [currentScene, setCurrentScene] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  
  // Premium TTS hook
  const premiumTTS = usePremiumTTS();

  // Get the best available voice
  const getBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;
    
    const preferredVoices = [
      'Samantha', 'Google US English', 'Google UK English Female',
      'Microsoft Zira', 'Microsoft David', 'Karen', 'Moira', 'Fiona',
      'Victoria', 'Alex', 'Daniel', 'Tessa',
    ];
    
    for (const preferred of preferredVoices) {
      const voice = voices.find(v => v.name.includes(preferred));
      if (voice) return voice;
    }
    
    const englishVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Natural') || !v.name.includes('eSpeak'))
    );
    if (englishVoice) return englishVoice;
    
    return voices.find(v => v.lang.startsWith('en')) || voices[0];
  }, []);

  // Load voices when available
  useEffect(() => {
    const loadVoices = () => {
      const voice = getBestVoice();
      if (voice) setSelectedVoice(voice);
    };
    
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [getBestVoice]);

  // Read aloud function - uses Premium TTS when enabled
  const speakText = useCallback((text) => {
    // Use premium TTS
    if (premiumTTS.usePremium) {
      premiumTTS.speak(text);
      return;
    }
    
    // Fallback to browser TTS
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  }, [selectedVoice, premiumTTS]);

  // Cancel speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
      premiumTTS.stop();
    };
  }, [premiumTTS]);

  // Map age groups to API format
  const getAgeGroupForAPI = (age) => {
    const ageMap = {
      'preschool': 'preschool',
      '3-5': 'preschool',
      'elementary': 'elementary',
      '6-8': 'elementary',
      'advanced': 'advanced',
      '9-12': 'advanced'
    };
    return ageMap[age] || 'elementary';
  };

  // Load story content - try API first, fallback to mockData
  useEffect(() => {
    const loadStoryContent = async () => {
      setLoading(true);
      try {
        const ageGroup = getAgeGroupForAPI(selectedAge);
        const response = await axios.get(`${API}/content/story/${ageGroup}/${selectedSubject}`);
        
        if (response.data.scenes && response.data.scenes.length > 0) {
          // API data available - use it
          setStoryData(response.data);
          console.log(`Loaded story from API for ${ageGroup}/${selectedSubject}`);
        } else {
          // No API data - fallback to mockData
          console.log('No API data for story, falling back to mockData');
          const data = getStoryData(selectedAge, selectedSubject);
          setStoryData(data);
        }
      } catch (error) {
        console.log('API error for story, falling back to mockData:', error.message);
        const data = getStoryData(selectedAge, selectedSubject);
        setStoryData(data);
      }
      setLoading(false);
    };

    loadStoryContent();
  }, [selectedAge, selectedSubject]);

  if (loading || !storyData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      }}>
        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl text-center max-w-md" style={{ borderRadius: '28px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-600 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800">Loading Story...</h2>
        </Card>
      </div>
    );
  }

  const currentSceneData = storyData.scenes[currentScene];

  const handleChoice = (choiceIndex) => {
    if (showFeedback) return;
    
    setSelectedChoice(choiceIndex);
    setShowFeedback(true);

    if (choiceIndex === currentSceneData.correctChoice) {
      const points = 15;
      setScore(score + points);
      addScore(points);
      setCorrectAnswers(correctAnswers + 1);
      // Award +2 points for correct answer
      awardCorrectAnswer();
      // Play correct sound
      if (soundEnabled) soundEffects.playCorrect();
    } else {
      // Play incorrect sound
      if (soundEnabled) soundEffects.playIncorrect();
    }
  };

  const nextScene = () => {
    if (currentScene + 1 < storyData.scenes.length) {
      setCurrentScene(currentScene + 1);
      setSelectedChoice(null);
      setShowFeedback(false);
    } else {
      // Award +10 points for completing the story
      awardLessonComplete('story');
      // Show celebration animation
      setShowCelebration(true);
      if (soundEnabled) soundEffects.playCelebration();
    }
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    navigate('/result', {
      state: {
        score,
        totalQuestions: storyData.scenes.length,
        correctAnswers,
        gameType: 'story'
      }
    });
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    }}>
      {/* Celebration Animation */}
      <CelebrationAnimation 
        show={showCelebration} 
        onComplete={handleCelebrationComplete}
        message="Story Complete!"
        duration={3500}
      />

      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/game-format')}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 md:gap-4">
            <PointsButton />
            <Button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <User className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => navigate('/rewards')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <Gift className="w-4 h-4" />
            </Button>
            <Card className="px-4 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <Trophy className="w-5 h-5 inline mr-2 text-yellow-600" />
              <span className="font-bold text-lg">{score}</span>
            </Card>
          </div>
        </div>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl" style={{ borderRadius: '28px' }}>
          <div className="text-center mb-8">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">{storyData.title}</h1>
            <p className="text-xl text-gray-600">Chapter {currentScene + 1} of {storyData.scenes.length}</p>
          </div>

          {/* Voice Settings Panel */}
          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {/* Sound Toggle */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  soundEnabled 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                <span className="text-sm font-medium">Sound {soundEnabled ? 'On' : 'Off'}</span>
              </button>

              {/* Premium Voice Toggle */}
              <button
                onClick={() => premiumTTS.togglePremium()}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  premiumTTS.usePremium 
                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-medium">Premium Voice {premiumTTS.usePremium ? 'On' : 'Off'}</span>
              </button>

              {/* Voice Selector Dropdown */}
              {premiumTTS.usePremium && (
                <div className="relative">
                  <button
                    onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 transition-all"
                  >
                    <Mic className="w-5 h-5" />
                    <span className="text-sm font-medium capitalize">{premiumTTS.selectedVoice}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showVoiceSettings ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Voice Dropdown */}
                  {showVoiceSettings && (
                    <div className="absolute top-full mt-2 right-0 bg-white rounded-2xl shadow-2xl border border-purple-100 overflow-hidden z-50 min-w-[240px]">
                      <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
                        <p className="text-sm font-semibold text-purple-800">Choose Your Narrator</p>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {PREMIUM_VOICES.map((voice) => (
                          <button
                            key={voice.id}
                            onClick={() => {
                              premiumTTS.setSelectedVoice(voice.id);
                              setShowVoiceSettings(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors flex items-center gap-3 ${
                              premiumTTS.selectedVoice === voice.id ? 'bg-purple-100' : ''
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              premiumTTS.selectedVoice === voice.id 
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {voice.name[0]}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{voice.name}</p>
                              <p className="text-xs text-gray-500">{voice.description}</p>
                            </div>
                            {premiumTTS.selectedVoice === voice.id && (
                              <span className="ml-auto text-purple-500">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mb-8">
            <Card className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 relative" style={{ borderRadius: '20px' }}>
              <button
                onClick={() => speakText(currentSceneData.text + ". " + currentSceneData.question)}
                className={`absolute top-4 right-4 p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                  premiumTTS.isPlaying || premiumTTS.isLoading || isSpeaking
                    ? 'bg-purple-500 text-white animate-pulse' 
                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                }`}
                title={premiumTTS.usePremium ? `Read with ${premiumTTS.selectedVoice} voice` : "Read story aloud"}
                disabled={premiumTTS.isLoading}
              >
                {premiumTTS.isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>
              <p className="text-2xl text-gray-800 leading-relaxed pr-12">
                {currentSceneData.text}
              </p>
            </Card>
          </div>

          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              {currentSceneData.question}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {currentSceneData.choices.map((choice, index) => {
              const isSelected = selectedChoice === index;
              const isCorrect = index === currentSceneData.correctChoice;
              const showCorrect = showFeedback && isCorrect;
              const showWrong = showFeedback && isSelected && !isCorrect;

              return (
                <Button
                  key={index}
                  onClick={() => handleChoice(index)}
                  disabled={showFeedback}
                  className={`p-6 text-lg font-semibold transition-all duration-300 hover:scale-105 ${
                    showCorrect ? 'bg-green-500 hover:bg-green-500' :
                    showWrong ? 'bg-red-500 hover:bg-red-500 shake-animation' :
                    'bg-gradient-to-r from-purple-500 to-pink-500'
                  }`}
                  style={{ borderRadius: '16px', minHeight: '80px' }}
                >
                  {choice}
                </Button>
              );
            })}
          </div>

          {showFeedback && (
            <div className="text-center">
              <Card className="p-6 mb-6 bg-blue-50" style={{ borderRadius: '16px' }}>
                <p className="text-lg text-gray-700">{currentSceneData.explanation}</p>
              </Card>
              <Button
                onClick={nextScene}
                size="lg"
                className="px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px'
                }}
              >
                {currentScene + 1 < storyData.scenes.length ? 'Continue Story' : 'Finish Story'}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StoryGame;
